/**
 * Home Feature - Japanese (ja-JP)
 */

import type { HomeLocale } from './types';

const jaJP: HomeLocale = {
    home: {
        title: 'AI で特許作成を加速',
        subtitle: '発明開示書から拒絶理由通知への応答まで、特許文書作成プロセス全体をインテリジェントに完成',

        tabs: {
            patent: '特許出願',
            disclosure: '発明開示書',
            oar: '拒絶理由対応',
        },

        cardTitles: {
            patent: '特許出願',
            disclosure: '発明開示書',
            oar: '拒絶理由通知への応答',
        },

        placeholders: {
            patent: '技術ソリューション、解決する問題、革新的な側面を説明してください。\n\n{\'@\'} を入力してテンプレートを使用するか、ファイルをアップロードすることもできます。',
            disclosure: '発明のアイデア、解決する技術的問題、主要な革新点を説明してください。\n\n{\'@\'} を入力してテンプレートを使用するか、ファイルをアップロードすることもできます。',
            oar: '拒絶理由通知書の内容をここに貼り付けるか、対応が必要な拒絶理由を説明してください。',
        },

        placeholder: {
            default: '発明を説明するか、{\'@\'} を入力してテンプレートを使用',
            template: '{\'@\'} を入力して特許テンプレートを参照',
            example1: '例：画像認識のための機械学習システム...',
            example2: '例：高容量の改良型バッテリー設計...',
            example3: '例：{\'@\'} USPTO ソフトウェア特許テンプレート',
        },

        workMode: {
            agent: 'エージェント',
            copilot: 'コパイロット',
        },

        actions: {
            addFiles: 'ファイル追加',
            addFilesTooltip: 'ファイルをアップロード（PDF、Word、TXT）',
            readOnlyTip: '読み取り専用権限のため、ファイルをアップロードできません',
            dropToAdd: 'ドロップしてファイルを追加',
            style: 'スタイル',
            styleTooltip: '作成スタイルを選択',
            template: 'テンプレート',
            templateTooltip: '特許テンプレートを選択',
            voiceInput: '音声入力',
            voiceInputTooltip: '音声入力',
            voiceInputSoon: '音声入力は近日公開予定...',
            generate: '開始',
            loggingIn: 'ログイン中...',
            startDrafting: '作成開始',
            draftingStyle: '作成スタイル',
            selectTemplate: 'テンプレートを選択',
            removeFile: '削除',
            loginNow: 'ログイン',
            changeTemplate: 'クリックしてテンプレートを変更',
            featureDisabledTip: 'この機能を使用する権限がありません。管理者にお問い合わせください',
            personalQuotaExhaustedTip: '個人クォータを使い切りました。管理者に追加割り当てを依頼してください',
        },

        upload: {
            dragOrClick: 'ファイルをここにドラッグするか、クリックしてアップロード',
            supportedFormats: 'PDF、Word、TXT、画像ファイルに対応',
        },

        quickStart: {
            title: 'よく使う作成タスクでクイックスタート：',
            disclosure: '開示書作成',
            specification: '明細書作成',
            claims: 'クレーム作成',
            oar: '応答作成',
            figures: '図面設計',
        },

        messages: {
            filesAdded: '{count} 件のファイルを追加しました',
            pleaseEnterContent: '内容を入力するかファイルをアップロードしてください',
            pleaseUploadFile: '拒絶理由通知ファイルをアップロードしてください',
            generating: '特許文書を生成中...',
            navigationFailed: 'ナビゲーション失敗',
            styleApplied: 'スタイルを適用しました：{name}',
            templateApplied: 'テンプレートを選択しました：{name}',
            mentionApplied: '{type}を選択しました：{name}',
            templateLoading: 'テンプレートを読み込み中：{name}',
            submitFailed: '送信に失敗しました。もう一度お試しください',
            invalidFile: '無効なファイル',
            uploadFailed: 'ファイルのアップロードに失敗しました。もう一度お試しください',
            filesUploading: 'ファイルをアップロード中です。お待ちください',
            fileStatusUploading: 'アップロード中...',
            fileStatusFailed: 'アップロード失敗',
            someFilesNotUploaded: '一部のファイルのアップロードに失敗しました。再度アップロードしてください',
            emptyInput: '内容を入力するかファイルをアップロードしてください',
            emptyOarInput: '特許出願番号を入力するかファイルをアップロードしてください',
            fileProcessed: 'ファイル {name} の解析が完了しました',
            fileProcessFailed: 'ファイル {name} の解析に失敗しました',
            fileProcessingTimeout: 'ファイル解析がタイムアウトしました。再度アップロードしてください',
            filesProcessing: 'ファイルを解析中です。お待ちください...',
            filesNotReady: '一部のファイルの解析が完了していません。完了後にタスクを作成してください',
            fileAlreadyExists: 'ファイル {name} は既に存在します。既存のファイルを使用します',
            defaultPromptForPatent: 'アップロードされた文書を解析し、技術開示書分析レポートと特許出願書類を生成してください。',
            defaultPromptForDisclosure: '発明開示書分析の標準フローに従ってファイルを処理し、発明開示書を生成してください',
            loginRequired: '作成を開始するにはログインしてください。作業はアカウントに保存されます。',
        },

        // タスクタイプとシナリオに基づいて自動選択される統一された初期プロンプト設定
        initialPrompts: {
            patent_draft: {
                withFiles: 'アップロードされた文書を解析し、技術開示書分析レポートと特許出願書類を生成してください。',
                empty: '特許出願の作成を開始してください',
            },
            disclosure: {
                withFiles: '発明開示書分析の標準フローに従ってファイルを処理し、発明開示書を生成してください',
                empty: '発明開示書の作成を開始してください',
            },
            prosecution: {
                withPatentNumber: '拒絶理由通知の分析を開始（出願番号：{patentNumber}）',
                withFiles: '拒絶理由通知の分析を開始',
                empty: '拒絶理由通知を分析し、応答を生成してください',
            },
        },

        mention: {
            title: 'テンプレート',
            selectType: 'タイプを選択',
            styleTitle: 'スタイル',
            priorArt: '先行技術',
            file: 'ファイル',
            document: 'ドキュメント',
            figure: '附図',
            loading: '読み込み中...',
            navigate: 'ナビゲート',
            select: '選択',
            close: '閉じる',
            noResults: '"{query}" に一致するテンプレートが見つかりません',
            tryDifferent: '別の検索語を試してください',
            applicationDate: '出願:',
            publicationDate: '公開:',
            docTypes: {
                patent_draft: '特許作成',
                disclosure: '技術開示書',
                prosecution: '審査意見対応',
            },
            docStatus: {
                draft: '下書き',
                in_review: 'レビュー中',
                approved: '承認済み',
                filed: '提出済み',
            },
            types: {
                template: {
                    label: 'テンプレート',
                    desc: '特許出願テンプレート',
                },
                style: {
                    label: '作成スタイル',
                    desc: '作成スタイル設定',
                },
                priorArt: {
                    label: '先行技術',
                    desc: '先行技術特許',
                },
                file: {
                    label: 'ファイル',
                    desc: '添付ファイル',
                },
                document: {
                    label: 'ドキュメント',
                    desc: 'プロジェクトドキュメント',
                },
                figure: {
                    label: '附図',
                    desc: 'プロジェクト附図',
                },
            },
            templates: {
                usptoUtilityPatent: 'USPTO 実用特許',
                usptoSoftwarePatent: 'USPTO ソフトウェア特許',
                epoPatentApplication: 'EPO 特許出願',
                pctInternationalApplication: 'PCT 国際出願',
                cnipaPatentApplication: 'CNIPA 特許出願',
            },
        },

        tooltips: {
            uploadFile: 'ファイルをアップロード',
            voiceInput: '音声入力',
        },

        patent: {
            jurisdiction: '出願地域を選択してください',
        },

        oar: {
            title: '拒絶理由通知への応答',
            projectTitle: '拒絶理由通知への応答 - {patentNumber}',
            projectTitleWithoutNumber: '拒絶理由通知への応答',
            initialPrompt: '拒絶理由通知の分析を開始',
            initialPromptWithNumber: '拒絶理由通知の分析を開始（出願番号：{patentNumber}）',
            confirmMessage: 'OAR analysis results confirmed, please continue processing',
            oarExtractList: 'OAR抽出リスト',
            rejectionTableTitle: '拒絶分析表',
            rejectionAnalysis: {
                title: '拒絶理由分析',
                status: {
                    extracting: 'データ抽出中',
                    processing: '処理中',
                },
                button: {
                    confirm: '確認して続行',
                    saving: '保存中',
                    addClause: '条項を追加',
                    showMore: 'もっと見る',
                    showLess: '折りたたむ',
                    export: 'Excelエクスポート',
                    exportSheetName: '拒絶理由分析',
                    viewDetail: '論理チェーンを見る',
                    delete: '削除',
                },
                detail: {
                    drawerTitle: '分析詳細',
                    examinerOpinion: '審査官意見',
                    featureComparison: '特徴比較',
                    claimFeature: '本願特徴',
                    priorArtFeature: '引用文献特徴',
                    priorArtLabel: '引用文献{no} · {name}',
                    noPriorArt: '引用文献なし',
                    strategy: '応答戦略',
                    strategyReason: '戦略根拠',
                    prevItem: '前へ',
                    nextItem: '次へ',
                },
                basicInfo: {
                    title: '基本情報',
                    application: '出願番号',
                    oaNo: '拒絶理由通知番号',
                    type: 'タイプ',
                    examiner: '審査官',
                    issueDate: '発行日',
                    priorArt: '引用文献',
                    applicationFiles: '出願書類',
                },
                workbench: {
                    caseTitleFallback: '特許出願応答戦略',
                    viewMaterials: '資料と審査履歴',
                    officeActionHistory: '審査履歴',
                    round: '審査ラウンド',
                    current: '現在',
                    claimsFile: '審査対象請求項',
                    overallStrategy: '全体応答戦略',
                    overallFallback: '案件全体の統合結論はありません。以下の各論点を確認してください。',
                    sectionTabsLabel: '拒絶分析ビュー',
                    caseOverviewTab: '案件概要',
                    documentComparisonTab: '相違特徴分析',
                    issueStrategiesTab: '論点別応答戦略',
                    issueView: '拒絶論点別',
                    documentComparison: '相違技術特徴の集約',
                    documentComparisonHint: '各論点の重複特徴を統合し、本願の記載位置と各引用文献の結論を対比します。',
                    relatedIssues: '関連論点',
                    mergeBasis: '統合根拠を表示',
                    comparisonConflict: '結論の不一致',
                    noDocumentComparison: '集約できる相違技術特徴はありません。',
                    combinedStrategy: '複合応答戦略',
                    issueCount: '審査論点',
                    amendmentCount: '補正提案',
                    riskCount: 'リスク',
                    issueNavigation: '審査論点ナビゲーション',
                    issueStrategy: '請求項戦略',
                    issue: '審査論点',
                    responseMethod: '応答方法',
                    claimAmendment: '請求項補正',
                    amendmentProvided: '補正案あり',
                    noAmendment: '補正なし',
                    examinerStartingPoint: '審査官の論理起点',
                    amendmentPlan: '請求項補正案',
                    amendmentPlanHint: '補正方法、原記載の根拠、権利範囲への影響を確認します。',
                    amendmentMethod: '補正方法',
                    scopeImpact: '権利範囲への影響',
                    amendmentBasis: '補正根拠',
                    amendmentReason: '補正を採用する理由',
                    viewAmendedClaims: '補正後請求項全文を表示',
                    threeStepMethod: '進歩性三段階分析',
                    threeStepHint: '構造化データに基づき、不足する結論は画面で補完しません。',
                    closestPriorArt: '最も近い先行技術の特定',
                    differencesAndProblem: '相違点と実際の技術的課題の特定',
                    distinguishingFeatures: '相違する特徴',
                    technicalEffects: '明細書記載の技術的効果',
                    objectiveTechnicalProblem: '実際に解決する技術的課題',
                    objectiveProblemGap: '独立した構造化項目がないため、画面では推測しません。',
                    technicalMotivation: '先行技術に示唆があるかの判断',
                    argumentPath: '技術的示唆と組合せ判断',
                    responseConclusion: '応答結論',
                    featureComparison: '主要特徴の比較',
                    featureComparisonHint: '請求項特徴と引用文献の結論を項目ごとに対応付けます。',
                    applicationFeature: '本願請求項の特徴',
                    priorArtConclusion: '引用文献との比較結論',
                    essentialFeature: '必須特徴',
                    viewPriorArtExcerpt: '引用文献の抜粋を表示',
                    embodimentBasis: '同一実施形態の根拠を表示',
                    noComparison: '表示できる特徴比較はありません。',
                    risks: '関連リスク',
                    risksHint: '重点確認が必要な案件固有のリスクです。',
                    supportingInformation: '裏付け情報',
                    supportingInformationHint: '本願概要、技術的効果、引用文献、検証メモをまとめます。',
                    applicationSummary: '本願技術の概要',
                    priorArtDocument: '引用文献',
                    viewClaimsSource: '審査対象請求項の原文を表示',
                    verificationNotes: '検証メモ',
                    historyConsistency: '履歴との整合性',
                    noPriorArt: '引用文献の概要はありません。',
                    notProvided: '未提供',
                    edit: '編集',
                    editOverallStrategy: '戦略サマリーを編集',
                    editFeatureSummary: '特徴を編集',
                    editComparison: '結論を編集',
                    editingInPlace: 'その場で編集中',
                    markdownSupported: 'Markdown 対応',
                    save: '保存',
                    cancel: 'キャンセル',
                    editResponseStrategy: '応答戦略を編集',
                    strategyReason: '戦略の理由',
                    amendedClaimText: '修正後の完全な請求項',
                    amendmentSourceType: '修正の出典',
                    sourceClaims: '出典請求項（カンマ区切り）',
                    sourceLocations: '根拠位置（セミコロン区切り）',
                    whyDependentInsufficient: '従属請求項の統合だけでは不十分な理由',
                    whyAmendmentOneInsufficient: '第一段階の修正では不十分な理由',
                    oneRiskPerLine: '1行につき1項目、最大4項目',
                    completeRequiredFields: '必須項目を入力してから保存してください',
                    markEssential: '必須特徴として設定',
                    evidenceReadonlyHint: '証拠原文と位置は読み取り専用です',
                    generateResponseDocument: '応答書を生成',
                    regenerateStrategy: '応答戦略を再生成',
                    regenerateScope: '再生成範囲',
                    currentIssue: '現在の問題',
                    allIssues: 'すべての問題',
                    regenerateRequirement: '追加要件',
                    regenerateRequirementPlaceholder: '例：ISSUE_001 の相違点と修正の必要性を再確認',
                    regenerateOverwriteHint: '再生成すると選択した問題の戦略が置き換わります。案件レベルの手動表現は保持されます。',
                    confirmRegenerate: '再生成を確認',
                    saveEditorFirst: '編集中の内容を保存またはキャンセルしてください',
                    openTargetDocumentFirst: '対象の応答書を開くか新規作成してください',
                    generateChatMessage: 'ユーザーは拒絶分析ページの編集を完了し、現在の区別技術特徴と応答戦略を確認しました。拒絶分析データ ID は {rejectionDataId} です。{refreshInstruction}現在のデータから応答書を生成し、バインド済みの対象文書に直接書き込んでください。再確認は不要で、Issue ごとの応答戦略は再生成しないでください。',
                    refreshIntegrationBeforeGenerate: 'Issue ごとの戦略が手動編集されています。文書生成前に integrate_only で案件レベルの統合のみを更新し、手動編集を保持してください。',
                    regenerateChatMessage: 'ユーザーは拒絶応答戦略の再生成を要求しています。拒絶分析データ ID は {rejectionDataId}、再生成範囲は {scope}、追加要件は {requirement} です。この範囲を再分析し、最新の拒絶分析結果を表示してください。応答書は生成せず、再確認も求めないでください。',
                    noAdditionalRequirement: 'なし',
                    sourceType: {
                        dependentClaim: '従属請求項',
                        claim: 'その他の請求項',
                        specification: '明細書',
                        drawing: '図面',
                        multipleSources: '複数の出典',
                    },
                    decision: {
                        argueNoAmend: '補正せず反論',
                        mergeDependent: '従属請求項を統合して反論',
                        amendOne: '第1段階の補正と反論',
                        amendTwo: '第2段階の補正と反論',
                        legacy: '既存の分析戦略を使用',
                    },
                },
                tabs: {
                    all: 'すべて',
                },
                tableHeaders: {
                    claim: 'クレーム',
                    statute: '法令',
                    claimAndStatute: 'クレーム/法令',
                    claimElement: 'クレーム要素',
                    examinerReasoning: '審査官の理由',
                    aiAnalysis: 'AI分析',
                    strategy: '応答戦略',
                },
                statusPills: {
                    agree: '同意',
                    unsure: '不明',
                    disagree: '不同意',
                },
                direction: {
                    agree: '同意',
                    unsure: '不明',
                    disagree: '不同意',
                },
                labels: {
                    direction: '応答方向',
                    strategy: '応答戦略',
                    claimAmendment: 'クレーム補正',
                    amendmentMethod: '補正方法',
                    amendmentBasis: '補正根拠',
                },
                needAmendment: {
                    yes: '補正',
                    no: '補正なし',
                    optional: 'AI判断',
                },
                tooltips: {
                    direction: '審査官の見解に同意するかどうか',
                    strategy: 'この拒絶理由に対する応答戦略',
                    claimAmendment: 'クレームの補正が必要かどうか',
                },
                placeholder: {
                    edit: 'クリックして編集...',
                    claimElement: 'クレーム要素を入力...',
                    examinerReasoning: '審査官の意見を入力...',
                    strategy: '応答戦略を入力...',
                    amendmentMethod: '補正方法を入力...',
                    amendmentBasis: '補正根拠を入力...',
                },
                emptyState: {
                    noData: '拒絶理由データなし',
                    hint: '抽出完了後、ここに拒絶理由分析が表示されます',
                },
                exportHeaders: {
                    issue: '審査論点',
                    claim: 'クレーム',
                    statute: '法令',
                    claimElement: 'クレーム要素',
                    examinerReasoning: '審査官の意見',
                    direction: '応答方向',
                    strategy: '応答戦略',
                    distinguishingFeatures: '区別技術特徴',
                    priorArt: '引用文献',
                    needAmendment: '補正要否',
                    amendmentMethod: '補正方法',
                    amendmentBasis: '補正根拠',
                    strategyReason: '戦略理由',
                    scopeImpact: '権利範囲への影響',
                    risks: 'リスク',
                    featureId: '特徴ID',
                    applicationLocation: '本願の位置',
                    comparisonStatus: '比較結論',
                    specificDifference: '具体的相違',
                    priorArtLocation: '引用文献の位置',
                },
                message: {
                    noDataId: 'データIDを取得できません',
                    noData: 'データがありません',
                    saveSuccess: '保存しました',
                    saveFailed: '保存に失敗しました',
                    deleteSuccess: '削除しました',
                },
            },
            region: {
                uspto: 'USPTO',
                epo: 'EPO',
                cnipa: 'CNIPA',
            },
            patentNumber: {
                label: '特許出願番号',
                placeholder: '審査中の特許の出願番号を入力して、最新の審査意見を取得',
            },
            uploadAction: {
                title: '拒絶理由通知をアップロード',
                choose: 'ファイルを選択',
                description: '回答が必要な審査意見のPDF/Wordファイルをアップロードしてください',
                uploading: 'アップロード中',
                addMore: 'さらにファイルをアップロード',
            },
            uploadedFiles: {
                title: 'アップロード済みファイル ({count})',
            },
            fileStatus: {
                preparing: 'ファイル準備中...',
                analyzing: '内容分析中...',
                finalizing: 'もうすぐ完了...',
                processing: '処理中...',
                estimatedTime: '推定 1-2 分',
                completed: '完了',
                failed: '解析失敗',
            },
            actions: {
                agent: 'エージェント',
                copilot: 'コパイロット',
                start: '開始',
            },
            search: {
                failed: '特許検索に失敗しました。もう一度お試しください',
                notFound: '特許が見つかりません',
            },
            patent: {
                publicationNumber: '公開番号：',
                publicationDate: '公開日：',
                currentApplicant: '[現在の]出願人：',
            },
            download: {
                searching: '拒絶理由通知PDFを検索中...',
                downloading: 'PDFダウンロードを開始しました。ブラウザのダウンロードを確認してください',
                success: 'PDFダウンロード成功',
                failed: 'PDFダウンロードに失敗しました。もう一度お試しください',
                notFound: '拒絶理由通知PDFが見つかりません。手動でアップロードできます',
                visitUsptoWebsite: 'USPTO Webサイトにアクセスしてダウンロード',
            },
            unsupportedRegion: 'この地域はまだサポートされていません',
            prosecutionTip: '特許出願番号を入力/審査意見PDFをアップロードして開始',
            rejectionDetail: {
                drawerTitle: '拒絶理由詳細',
                examinerOpinion: '審査官意見',
                featureComparison: '特徴比較',
                claimFeature: 'クレーム特徴',
                specificationSource: '明細書原文',
                priorArtFeature: '引用文献特徴',
                priorArtLabel: '引用文献{no}（{name}）',
                noPriorArt: '引用文献情報がありません',
                priorArtSource: '{name} 原文',
                distinguishingFeatures: '区別技術特徴',
                aiStrategy: 'AI 応答戦略',
                strategyReason: '戦略根拠',
                crossValidation: '交差検証',
                prevItem: '前へ',
                nextItem: '次へ',
                direction: {
                    agree: '同意',
                    unsure: '保留',
                    disagree: '不同意',
                },
                claimAmendment: {
                    title: 'クレーム補正提案',
                    needAmendment: '補正要否',
                    yes: '補正',
                    no: '補正なし',
                    optional: 'AI判断',
                    method: '補正方法',
                    basis: '補正根拠',
                },
            },
        },

        draftingStyle: {
            title: '作成スタイルを選択',
            stylesHeader: 'スタイル',
            previewHeader: 'プレビュー',
            cancel: 'キャンセル',
            apply: 'スタイルを適用',
            loading: '読み込み中...',
            loadingPreview: 'プレビュー読み込み中...',
            loadError: 'スタイル一覧の読み込みに失敗しました',
            noStyles: '利用可能なスタイルがありません',
            noPreview: 'プレビューがありません',
            pleaseSelect: 'まずスタイルを選択してください',
            used: '{count}回使用',
            styles: {
                standard: {
                    name: '標準フォーマット',
                    description: '番号付き段落と正式な言語を使用した従来の特許フォーマット',
                },
                detailed: {
                    name: '詳細技術',
                    description: '詳細な実施例を含む包括的な技術説明',
                },
                concise: {
                    name: '簡潔クレーム重視',
                    description: '明確で強力なクレーム言語を強調した簡潔なフォーマット',
                },
                international: {
                    name: 'PCT 国際出願',
                    description: 'PCT に基づく国際特許出願に最適化されたフォーマット',
                },
                continuation: {
                    name: '継続出願',
                    description: '親出願を参照する継続出願または分割出願のフォーマット',
                },
            },
        },

        templateSelection: {
            title: 'テンプレートを選択',
            templatesHeader: 'テンプレート一覧',
            previewHeader: 'プレビュー',
            cancel: 'キャンセル',
            apply: 'テンプレートを適用',
            loading: '読み込み中...',
            loadingPreview: 'プレビューを読み込み中...',
            loadError: 'テンプレート一覧の読み込みに失敗しました',
            loadDetailError: 'テンプレート詳細の読み込みに失敗しました',
            noTemplates: '利用可能なテンプレートがありません',
            noPreview: 'プレビューがありません',
            pleaseSelect: '最初にテンプレートを選択してください',
        },

        documentModifications: {
            title: '文書変更を適用済み',
            summary: '合計 {count} 件の変更',
            streamingSummary: '変更を生成中、{count} 件を検出',
            streamingTitle: 'AI が文書を編集中です',
            streamingDesc: '変更内容を受信中です。完了後、バックエンドでマージして最新バージョンに更新します。',
            streamPreview: 'リアルタイム変更ストリーム',
            appliedSummary: '{count} 件の変更を適用済み',
            appliedTitle: '変更は自動的に適用されました',
            appliedDesc: 'バックエンドで編集をマージし、新しいバージョンを作成しました。エディターは最新内容に更新されます。',
            emptyState: '変更詳細がありません',
            noContent: '新しい内容はありません',
            oldContent: '旧テキスト:',
            actions: {
                insert: '挿入',
                replace: '置換',
                delete: '削除',
            },
            tableHeaders: {
                index: '#',
                documentId: '文書 ID',
                action: '操作',
                dataId: 'data-id',
                content: '変更内容',
                oldContent: '旧テキスト',
            },
            exportExcel: 'Excelエクスポート',
        },
    },
};

export default jaJP;
